import React from 'react';
import EditorField from './_EditorField';
import DataBindingPanel from '../DataBindingPanel';
import DataSectionPanel from './DataSectionPanel';
import { useTheme } from '../../../theme/ThemeContext';

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function ColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button key={p.color + p.label} type="button" title={p.label} onClick={() => onChange(p.color)}
          style={{ width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0, cursor: 'pointer',
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            background: p.color === '' ? 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 0 0 / 10px 10px' : p.color,
          }} />
      ))}
      <input type="color" value={value && value !== '' ? value : '#ffffff'} onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      {value && value !== '' && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value !== '' && <button type="button" onClick={() => onChange('')} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Reset</button>}
    </div>
  );
}

export default function DataTableEditor({ section, dispatch, appId }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const update = patch => dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  const updateBinding = patch => dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch });

  const primaryHex = theme.primary || '#4e2e80';
  const HEADER_PRESETS = [
    { label: 'Default', color: '#f8fafc' },
    { label: 'White',   color: '#ffffff' },
    { label: 'Primary', color: primaryHex },
    { label: 'Dark',    color: '#1e293b' },
  ];

  const tableSlug = section.data_binding?.table_slug;

  return (
    <div>
      <DataBindingPanel appId={appId} binding={section.data_binding} onChange={updateBinding} />

      <SectionDivider label="Table Style" />

      <EditorField label="Table Style">
        <select className="form-select form-select-sm" value={cfg.table_style || 'default'}
          onChange={e => update({ table_style: e.target.value })}>
          <option value="default">Default</option>
          <option value="striped">Striped Rows</option>
          <option value="bordered">Bordered</option>
          <option value="compact">Compact</option>
          <option value="minimal">Minimal (no borders)</option>
        </select>
      </EditorField>

      <EditorField label="Header Background">
        <ColorPicker value={cfg.header_bg_color || '#f8fafc'} onChange={val => update({ header_bg_color: val })} presets={HEADER_PRESETS} />
      </EditorField>

      <EditorField label="Header Text Color">
        <ColorPicker value={cfg.header_text_color || ''} onChange={val => update({ header_text_color: val })}
          presets={[
            { label: 'Default', color: '' },
            { label: 'Dark',    color: '#1e293b' },
            { label: 'White',   color: '#ffffff' },
            { label: 'Primary', color: primaryHex },
          ]}
        />
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="showSearch"
            checked={cfg.show_search !== false} onChange={e => update({ show_search: e.target.checked })} />
          <label className="form-check-label" htmlFor="showSearch" style={{ fontSize: 13 }}>Show search bar</label>
        </div>
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="showRowNumbers"
            checked={cfg.show_row_numbers || false} onChange={e => update({ show_row_numbers: e.target.checked })} />
          <label className="form-check-label" htmlFor="showRowNumbers" style={{ fontSize: 13 }}>Show row numbers</label>
        </div>
      </EditorField>

      <DataSectionPanel cfg={cfg} updateCfg={update} tableSlug={tableSlug} />
    </div>
  );
}
